import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/components/nexus/feedback/toaster'
import { clearBrandOverride } from '@/lib/brand'
import {
  IMPERSONATION_STOP_REFUSED_MESSAGE,
  IMPERSONATION_STOP_UNVERIFIED_MESSAGE,
  impersonationCleared,
} from '@/lib/impersonation'
import { trpc } from '@/trpc/client'
import { verifyEffectiveIdentity } from './verify-effective-identity'

/** What a surface may do with a live impersonation. Deliberately not the mutation. */
export interface StopImpersonation {
  stop: () => void
  isPending: boolean
}

/**
 * Stop impersonating, and only navigate once the clear is OBSERVED.
 *
 * The mirror of `useStartImpersonation`, and it exists for the same reason one
 * direction over: `impersonation.stop` deletes the cookie pair as a side effect
 * of its 200, so the mutation resolving proves the server ISSUED the clear —
 * never that the browser APPLIED it.
 *
 * What that costs when it goes wrong — the 403 on arrival, the Stop button that
 * 200s without changing anything, the pair riding its full 24h `maxAge` — is
 * argued once, on `impersonationCleared` in `@/lib/impersonation`. It is not
 * restated here.
 *
 * **A full document load, not a router navigation.** The whole point is to
 * re-issue every request under the cleared jar; a client-side route change
 * would keep the pre-stop cache and the pre-stop shell.
 *
 * **`replace`, not `href`.** `href` pushes, which leaves the entry the stop is
 * leaving sitting directly behind the destination — and for a business
 * impersonation that entry is `/app?brand=business`, written there by the
 * in-place promotion. One Back press loads it as a fresh document, and
 * `detectBrand()` ranks the query param above everything, so it re-writes the
 * override this hook just cleared. `replace` consumes that entry instead of
 * stacking on it. It does not reach an entry the admin navigated PAST during
 * the impersonation; in-SPA navigation drops the param, so the promoted entry
 * is the one that carries it.
 *
 * **On `unknown` it does not navigate, and that is the deliberate half.** The
 * stop may well have worked, and refusing to move leaves the admin on a page
 * that at least renders. Navigating on an unconfirmed clear is what puts them
 * in front of a 403 with no way to tell why.
 *
 * **It returns a narrowed object, not the `useMutation` result** — the same
 * reason `useStartImpersonation` does. TanStack fires a per-call `onSuccess`
 * passed to `mutate(vars, opts)` independently of the one here, and this hook's
 * refusal is a `return`, so a surface writing
 * `mutate(undefined, { onSuccess: () => { location.href = '/ops/users' } })`
 * would get the toast AND the navigation, reinstating the exact defect. That
 * spelling is now a compile error.
 *
 * **It is not a general guarantee.** A caller can still write
 * `stop(); location.href = '/ops/users'` on the next line, which compiles and
 * which `impersonation-callers.test.ts` cannot see either. What is closed is
 * the shape that looks correct while being wrong.
 */
export function useStopImpersonation(): StopImpersonation {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    trpc.impersonation.stop.mutationOptions({
      onSuccess: async () => {
        const verdict = await verifyEffectiveIdentity(
          queryClient,
          impersonationCleared,
        )
        if (verdict === 'applied') {
          // Impersonating a business customer promotes the admin in place, and
          // `detectBrand()` remembers the `?brand=business` that promotion put
          // in the URL. The override outranks the host map, so without this the
          // admin's own next visit to `/app` renders Business — a superadmin
          // passes `canUseBusiness`, so nothing downstream corrects it. Cleared
          // on `applied` only: the other two verdicts leave the impersonation
          // possibly live, and the brand should follow it.
          //
          // Deliberately UNSCOPED — it fires even where the target was an
          // ordinary customer and nothing was ever promoted. A promoted
          // override and one an operator set by hand are the same two
          // characters in the same key, so there is nothing to narrow on: the
          // cost of the wide clear is a developer on localhost losing a
          // `?brand=business` they chose, which is one URL away from being back.
          //
          // **This is one door of several, and it is not the last word.** A
          // refused pair (`createContext` clears it server-side) and the pair
          // reaching its 24h `maxAge` both end an impersonation with no client
          // code running at all, so neither reaches here; `useSignOut` clears
          // the override for the exit it owns. Nothing covers expiry.
          clearBrandOverride()
          window.location.replace('/ops/users')
          return
        }
        toast.error(
          verdict === 'refused'
            ? IMPERSONATION_STOP_REFUSED_MESSAGE
            : IMPERSONATION_STOP_UNVERIFIED_MESSAGE,
        )
      },
      onError: (err) =>
        toast.error(`Failed to stop impersonation: ${err.message}`),
    }),
  )

  return {
    stop: () => mutation.mutate(),
    isPending: mutation.isPending,
  }
}
